/**
 * API standart hata yanıtı (backend ile aynı alanlar).
 */
import i18n from '../config/i18n';

export interface ApiErrorResponse {
  code: string;
  message: string;
  correlationId?: string;
}

/**
 * Uygulama tarafında kullanılan hata; toast ve bildirim için gerekli alanları taşır.
 */
export interface AppErrorOptions {
  /** Kullanıcıya gösterilecek mesaj (code'dan çevrilmiş veya API message) */
  userMessage: string;
  /** API'den gelen kod (ERR_VALIDATION, ERR_NOT_FOUND, ERR_INTERNAL vb.) */
  code?: string;
  /** İstek eşleştirme ID'si; bildir butonunda kullanılır */
  correlationId?: string;
  /** true ise "sistemsel hata" uyarısı ve Bildir butonu gösterilir */
  isUnexpected?: boolean;
}

/** Bilinen API hata kodları (backend ile eşleşmeli). */
export const KNOWN_ERROR_CODES = new Set([
  'ERR_VALIDATION',
  'ERR_NOT_FOUND',
  'ERR_CONFLICT',
  'ERR_INTERNAL',
  'ERR_SERVICE_UNAVAILABLE',
  'ERR_BAD_REQUEST',
  'ERR_UNAUTHORIZED',
  'ERR_FORBIDDEN',
  'ERR_PLAN_INSUFFICIENT',
]);

function translateApiErrorKey(key: string): string {
  const lng = i18n.language || 'en';
  const msg =
    i18n.getResource(lng, 'translation', key) ??
    i18n.getResource('en', 'translation', key);
  return typeof msg === 'string' ? msg : '';
}

/**
 * Code için kullanıcı mesajı döner.
 * API'den gelen message (Accept-Language ile lokalize) varsa onu kullanır;
 * yoksa i18n apiError.* fallback.
 */
export function getUserMessageForCode(
  code: string,
  fallbackMessage?: string
): string {
  if (fallbackMessage && fallbackMessage.trim()) {
    return fallbackMessage.trim();
  }
  const translated = translateApiErrorKey(`apiError.${code}`);
  if (translated) return translated;
  return (
    translateApiErrorKey('apiError.ERR_INTERNAL') ||
    'An unexpected error occurred. Please try again later.'
  );
}

/** 5xx veya bilinmeyen kod = beklenmeyen (sistemsel) hata. */
export function isUnexpectedError(code: string, httpStatus: number): boolean {
  if (httpStatus >= 500) return true;
  return code === 'ERR_INTERNAL' || !KNOWN_ERROR_CODES.has(code);
}

/** @deprecated Prefer getUserMessageForCode + i18n; kept for any direct imports. */
export const ERROR_CODE_MESSAGES: Record<string, string> = {
  ERR_VALIDATION: 'Please check your input.',
  ERR_NOT_FOUND: 'The requested resource was not found.',
  ERR_CONFLICT: 'This operation could not be completed due to a conflict.',
  ERR_INTERNAL: 'An unexpected error occurred. Please try again later.',
  ERR_SERVICE_UNAVAILABLE: 'The service is currently unavailable. Please try again later.',
  ERR_BAD_REQUEST: 'Invalid request.',
  ERR_UNAUTHORIZED: 'You need to sign in.',
  ERR_FORBIDDEN: 'You do not have permission for this action.',
  ERR_PLAN_INSUFFICIENT: 'Your plan does not allow this action. Upgrade your plan.',
};
